import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import SeoHead, { ORGANIZATION_LD } from '../components/SeoHead'
import { api, ApiError, fileToBase64, type VerifyDocumentInfo, type VerifyResponse } from '../lib/api'

type Status = 'idle' | 'uploading' | 'needs-original' | 'success' | 'error'

export default function VerifyPage() {
  const [status,         setStatus]         = useState<Status>('idle')
  const [result,         setResult]         = useState<VerifyResponse | null>(null)
  const [error,          setError]          = useState<string | null>(null)
  const [fileName,       setFileName]       = useState<string>('')
  const [signatureFile,  setSignatureFile]  = useState<File | null>(null)
  const [dragOver,       setDragOver]       = useState(false)
  const [dragOver2,      setDragOver2]      = useState(false)
  const inputRef  = useRef<HTMLInputElement | null>(null)
  const input2Ref = useRef<HTMLInputElement | null>(null)

  const reset = () => {
    setStatus('idle')
    setResult(null)
    setError(null)
    setFileName('')
    setSignatureFile(null)
  }

  /** İlk dosya — kullanıcı ne tür gönderdiği belli değil (attached CMS, XAdES, ya da detached imza). */
  const handleFile = useCallback(async (file: File) => {
    setFileName(file.name)
    setStatus('uploading')
    setResult(null)
    setError(null)
    setSignatureFile(null)

    try {
      const resp = await api.verifyFile(file)

      // Backend "detached signature requires externally-supplied content" diyorsa,
      // bu bir bağımsız CMS imzasıdır; orijinal belgeyi sor. PAdES'te imza zaten PDF'in
      // içindedir — orada "detached" kelimesi SubFilter'dan gelir, ikinci dosya istenmemeli.
      const msg = (resp.errorMessage ?? '').toLowerCase()
      if (!resp.isValid
          && resp.format !== 'PAdES'
          && (msg.includes('detached') || msg.includes('externally-supplied'))) {
        setSignatureFile(file)
        setStatus('needs-original')
        return
      }

      setResult(resp)
      setStatus('success')
    } catch (err) {
      setStatus('error')
      setError(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err))
    }
  }, [])

  /** İkinci dosya — orijinal belge; detached imza + orijinal birlikte doğrulanır. */
  const handleOriginalFile = useCallback(async (originalFile: File) => {
    if (!signatureFile) return
    setStatus('uploading')
    setError(null)
    try {
      // Backend kontratı: content = orijinal belge, detachedSignature = CMS imza bytes
      const [originalB64, signatureB64] = await Promise.all([
        fileToBase64(originalFile),
        fileToBase64(signatureFile),
      ])
      const resp = await api.verifyBase64(originalB64, signatureB64)
      setResult(resp)
      setFileName(`${signatureFile.name} + ${originalFile.name}`)
      setStatus('success')
    } catch (err) {
      setStatus('error')
      setError(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err))
    }
  }, [signatureFile])

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) void handleFile(f)
  }, [handleFile])

  const onDrop2 = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver2(false)
    const f = e.dataTransfer.files?.[0]
    if (f) void handleOriginalFile(f)
  }, [handleOriginalFile])

  return (
    <>
      <SeoHead
        title="E-imza doğrulama — imzalı PDF, .p7s ve XML dosyalarını ücretsiz doğrulayın"
        description="İmzalı PDF (PAdES), CAdES (.p7s, .cms, .p7m) veya XAdES (.xml) dosyanızı yükleyin; imzanın geçerliliği, imzalayan sertifika, zaman damgası ve uzun dönem doğrulama bilgileri anında görünsün. Hesap gerekmez, telemetri yok."
        path="/verify"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'W.Sign İmza Doğrulayıcı',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            url: 'https://sign.wsoft.tr/verify',
            description:
              'Elektronik imzalı dosyaların geçerliliğini, imzalayan sertifikayı, zaman damgasını ve uzun dönem doğrulama verilerini tarayıcıdan kontrol eden ücretsiz araç.',
            inLanguage: 'tr-TR',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'TRY' },
            provider: ORGANIZATION_LD,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Ayrık (detached) e-imza nasıl doğrulanır?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '.p7s uzantılı dosyalarda imza belgeden ayrıdır; doğrulama için hem imza dosyasını hem de orijinal belgeyi vermeniz gerekir.',
                },
              },
              {
                '@type': 'Question',
                name: 'Doğrulamak için yüklediğim belge saklanıyor mu?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hayır. Gönderilen dosya işlenip sonucu döndürülür, saklanmaz. Hesap açmanız gerekmez ve kullanım telemetrisi toplanmaz.',
                },
              },
              {
                '@type': 'Question',
                name: 'Sertifikanın süresi dolmuşsa imza geçersiz mi olur?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Zorunlu olarak değil. İmza uzun dönemli seviyedeyse veya zaman damgası taşıyorsa imzanın atıldığı anda sertifikanın geçerli olduğu kanıtlanabilir; süre dolması imzayı geçersiz kılmaz. Temel (BES) seviyede bu kanıt yoktur.',
                },
              },
              {
                '@type': 'Question',
                name: 'İmza doğrulamayı kendi sistemime bağlayabilir miyim?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Evet. Doğrulama ucu herkese açıktır ve API anahtarı istemez; imzalı PDF, CAdES ve XAdES dosyalarını kabul eder.',
                },
              },
            ],
          },
        ]}
      />
      <section className="container-page py-12 md:py-16">
        <h1 className="text-3xl md:text-4xl font-semibold">İmza Doğrulayıcı</h1>
        <p className="mt-2 text-[var(--color-fg-muted)] max-w-2xl">
          İmzalı PDF (<code>.pdf</code> — PAdES), CAdES (<code>.p7s</code>, <code>.cms</code>, <code>.p7m</code>)
          veya XAdES (<code>.xml</code>) dosyasını yükle. İmza geçerliliği, imzalayan sertifika, zaman damgası
          ve uzun dönem doğrulama bilgileri anında görüntülenir. Belgede birden fazla imza varsa hepsi listelenir.
          Hesap gerekmez, telemetri yok.
        </p>

        {status !== 'needs-original' && (
          <div
            className={`mt-8 rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
              dragOver
                ? 'border-[var(--color-accent)] bg-[var(--color-accent-wash)]'
                : 'border-[var(--color-border)] bg-[var(--color-bg-elev)]'
            }`}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <p className="text-base font-medium">Dosyayı buraya sürükle</p>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">veya</p>
            <button
              onClick={() => inputRef.current?.click()}
              className="btn-primary mt-3"
              type="button"
              disabled={status === 'uploading'}
            >
              {status === 'uploading' ? 'Doğrulanıyor…' : 'Dosya seç'}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".p7s,.cms,.p7m,.imz,.xml,.pdf,.xades"
              onChange={e => { const f = e.target.files?.[0]; if (f) void handleFile(f) }}
              className="hidden"
            />
            {fileName && status === 'uploading' && (
              <p className="mt-3 text-xs font-mono text-[var(--color-fg-dim)]">{fileName}</p>
            )}
          </div>
        )}

        {/* Detached imza — orijinal belge isteme akışı */}
        {status === 'needs-original' && signatureFile && (
          <div className="card mt-8 border-blue-200 bg-blue-50">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="badge badge-info">Detached imza tespit edildi</span>
                <h3 className="mt-3 text-lg font-semibold text-blue-900">
                  Orijinal belgeyi de yükle
                </h3>
                <p className="mt-2 text-sm text-blue-800 max-w-xl">
                  Bu <code className="font-mono">.p7s</code> dosyası bağımsız (detached) bir
                  CMS imzasıdır — sadece imzayı taşır, orijinal belge ayrıdır. Doğrulama için
                  iki dosya birlikte gerekir.
                </p>
                <p className="mt-3 text-xs font-mono text-blue-700">
                  İmza dosyası: <strong>{signatureFile.name}</strong> ({formatBytes(signatureFile.size)})
                </p>
              </div>
              <button onClick={reset} type="button" className="btn-ghost text-xs">
                Sıfırla
              </button>
            </div>

            <div
              className={`mt-6 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                dragOver2
                  ? 'border-blue-500 bg-white'
                  : 'border-blue-300 bg-white/60'
              }`}
              onDragOver={e => { e.preventDefault(); setDragOver2(true) }}
              onDragLeave={() => setDragOver2(false)}
              onDrop={onDrop2}
            >
              <p className="text-sm font-medium text-blue-900">Orijinal belgeyi buraya sürükle</p>
              <p className="mt-1 text-xs text-blue-700">veya</p>
              <button
                onClick={() => input2Ref.current?.click()}
                className="btn-primary mt-3"
                type="button"
              >
                Orijinal belgeyi seç
              </button>
              <input
                ref={input2Ref}
                type="file"
                onChange={e => { const f = e.target.files?.[0]; if (f) void handleOriginalFile(f) }}
                className="hidden"
              />
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="card mt-6 border-red-200 bg-red-50">
            <h3 className="font-semibold text-red-800">Hata</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button onClick={reset} type="button" className="btn-ghost mt-3 text-xs">
              Sıfırla
            </button>
          </div>
        )}

        {status === 'success' && result && <VerifyResultCard fileName={fileName} result={result} onReset={reset} />}

        <div className="mt-10 grid gap-4 md:grid-cols-4">
          <InfoCard
            title="PAdES"
            desc=".pdf — imza PDF içine gömülü, tek dosya"
          />
          <InfoCard
            title="CAdES Attached"
            desc=".p7m / .cms — içerik gömülü, tek dosya"
          />
          <InfoCard
            title="CAdES Detached"
            desc=".p7s — imza ayrı, orijinal belge de gerekir"
          />
          <InfoCard
            title="XAdES"
            desc=".xml — enveloped XML imzası"
          />
        </div>
      </section>

      {/* Açıklayıcı içerik — aracı kullanan kişinin sonucu yorumlayabilmesi için.
          Aynı zamanda bu sayfanın arama motorlarında karşılığı olan tek metin. */}
      <section className="container-page pb-8">
        <h2 className="text-2xl font-semibold">Doğrulama neyi kanıtlar?</h2>
        <p className="mt-3 max-w-3xl text-[var(--color-fg-muted)]">
          Bir elektronik imzanın doğrulanması iki ayrı soruyu birden yanıtlar:
          belgenin <strong>imzalandıktan sonra değişmediğini</strong> ve imzanın{' '}
          <strong>kime ait olduğunu</strong>. İkisi de matematiksel olarak kontrol edilir;
          "imza geçerli" sonucu, belgenin tek bir baytının bile değişmediği anlamına gelir.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <InfoCard
            title="Bütünlük"
            desc="Belgenin imzalandığı andan bu yana değişmediği. Tek bayt değişse imza geçersiz olur."
          />
          <InfoCard
            title="Kimlik"
            desc="İmzalayanın sertifikası: ad, kurum, sertifikayı veren makam ve geçerlilik aralığı."
          />
          <InfoCard
            title="Zaman"
            desc="Zaman damgası varsa imzanın hangi anda atıldığı bağımsız bir otorite tarafından kanıtlanır."
          />
        </div>
      </section>

      <section className="container-page pb-8">
        <h2 className="text-2xl font-semibold">İmza seviyeleri ne anlama geliyor?</h2>
        <p className="mt-3 max-w-3xl text-[var(--color-fg-muted)]">
          Doğrulama sonucunda gördüğünüz seviye, imzanın <strong>ne kadar süre</strong>{' '}
          doğrulanabilir kalacağını belirler.
        </p>
        <div className="mt-6 space-y-4 max-w-3xl">
          <LevelRow
            level="BES / B-B"
            title="Temel imza"
            body="İmza ve imzalayan sertifika vardır. Doğrulama, sertifikanın hâlâ geçerli olmasına ve iptal bilgisine çevrimiçi ulaşılabilmesine bağlıdır."
          />
          <LevelRow
            level="T"
            title="Zaman damgalı imza"
            body="İmzaya bağımsız bir zaman damgası eklenir. İmzanın atıldığı an kanıtlanır; sertifika sonradan iptal edilse bile imza anı tartışmasız kalır."
          />
          <LevelRow
            level="Uzun dönemli"
            title="ESXLong · X-L · PAdES-B-LT"
            body="Sertifika zinciri ve iptal bilgisi imzanın içine gömülür. Sertifikanın süresi dolduktan veya iptal listesi erişilemez olduktan sonra da, çevrimdışı olarak doğrulanabilir. Arşivlenecek belgelerde aranan seviye budur."
          />
        </div>
      </section>

      <section className="container-page pb-20">
        <h2 className="text-2xl font-semibold">Sık sorulanlar</h2>
        <div className="mt-6 space-y-4 max-w-3xl">
          <Faq q="Ayrık (detached) imzayı nasıl doğrularım?">
            <code className="rounded bg-[var(--color-bg-soft)] px-1.5 py-0.5 font-mono text-[0.85em]">.p7s</code>{' '}
            uzantılı dosyalarda imza belgeden ayrıdır; doğrulama için hem imza dosyasını hem
            orijinal belgeyi vermeniz gerekir. Araç imza dosyasını yüklediğinizde orijinali
            ayrıca ister.
          </Faq>
          <Faq q="Belgem yüklediğim yere kaydediliyor mu?">
            Hayır. Doğrulama için gönderilen dosya işlenip sonucu döndürülür, saklanmaz. Hesap
            açmanız gerekmez ve kullanım telemetrisi toplanmaz — ayrıntısı{' '}
            <Link to="/gizlilik" className="text-[var(--color-accent)] hover:underline">gizlilik sayfasında</Link>.
          </Faq>
          <Faq q="İmza geçerli çıktı ama sertifikanın süresi dolmuş, sorun mu?">
            Zorunlu olarak değil. İmza uzun dönemli seviyedeyse veya zaman damgası taşıyorsa,
            imzanın atıldığı anda sertifikanın geçerli olduğu kanıtlanabilir; süre dolması
            imzayı geçersiz kılmaz. Temel (BES) seviyede ise bu kanıt yoktur.
          </Faq>
          <Faq q="Doğrulamayı kendi sistemime bağlayabilir miyim?">
            Evet, doğrulama ucu herkese açıktır ve API anahtarı istemez. Ayrıntısı{' '}
            <Link to="/docs" className="text-[var(--color-accent)] hover:underline">geliştirici dokümanında</Link>.
          </Faq>
        </div>

        <div className="card mt-8 bg-[var(--color-accent-wash)] border-[var(--color-accent)]/20 flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold">İmza da atmanız gerekiyor mu?</h2>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
              Nitelikli e-imza kartınızla tarayıcıdan imzalayın ya da belgenizi karşı tarafa
              uzaktan imzalatın.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link to="/hash-sign" className="btn-primary">İmzala</Link>
            <Link to="/login" className="btn-ghost">İmzalatmaya başla</Link>
          </div>
        </div>
      </section>
    </>
  )
}

function LevelRow({ level, title, body }: { level: string; title: string; body: string }) {
  return (
    <div className="card flex flex-col gap-1 sm:flex-row sm:gap-4">
      <span className="badge shrink-0 self-start font-mono">{level}</span>
      <div>
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{body}</p>
      </div>
    </div>
  )
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="card group">
      <summary className="cursor-pointer list-none font-medium marker:hidden">
        <span className="flex items-center justify-between gap-2">
          {q}
          <span className="text-[var(--color-fg-dim)] transition-transform group-open:rotate-45">+</span>
        </span>
      </summary>
      <div className="mt-3 text-sm text-[var(--color-fg-muted)]">{children}</div>
    </details>
  )
}

function VerifyResultCard({ fileName, result, onReset }: {
  fileName: string
  result: VerifyResponse
  onReset: () => void
}) {
  return (
    <div className="card mt-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-mono text-[var(--color-fg-dim)]">{fileName}</p>
          <h3 className="mt-1 text-lg font-semibold">
            {result.isValid
              ? <span className="text-emerald-600">İmza geçerli</span>
              : <span className="text-red-600">İmza geçersiz</span>}
          </h3>
          <p className="text-sm text-[var(--color-fg-muted)] mt-1">
            Format: <strong>{result.format}</strong> · Durum: {result.status}
            {result.errorMessage && <> · {result.errorMessage}</>}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`badge ${result.isValid ? 'badge-ok' : 'badge-err'}`}>
            {result.isValid ? '✓ Valid' : '✗ Invalid'}
          </span>
          <button onClick={onReset} type="button" className="btn-ghost text-xs">
            Yeni doğrulama
          </button>
        </div>
      </div>

      {result.document && <DocumentSummary doc={result.document} />}

      {result.documentTimestamps && result.documentTimestamps.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
            Belge Zaman Damgaları ({result.documentTimestamps.length})
          </h4>
          {result.documentTimestamps.map(ts => (
            <div key={ts.fieldName} className="rounded border border-[var(--color-border)] p-3 text-xs">
              <p className="font-medium">
                {ts.fieldName}
                {' · '}
                <span className={ts.isValid ? 'text-emerald-600' : 'text-red-600'}>
                  {ts.isValid ? 'geçerli' : 'geçersiz'}
                </span>
              </p>
              <p className="mt-1 text-[var(--color-fg-muted)]">
                {ts.time ? new Date(ts.time).toLocaleString('tr-TR') : '—'}
                {ts.tsaSubject && <> · TSA: {ts.tsaSubject}</>}
              </p>
              {ts.errorMessage && <p className="mt-1 text-red-600">{ts.errorMessage}</p>}
            </div>
          ))}
        </div>
      )}

      {result.signers.length > 0 && (
        <div className="mt-6 space-y-4">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
            İmzalayanlar ({result.signers.length})
          </h4>
          {result.signers.map((s, idx) => (
            <div key={idx} className="rounded border border-[var(--color-border)] p-4">
              {s.pdf && (
                <p className="mb-2 text-[11px] font-mono text-[var(--color-fg-dim)]">
                  {s.pdf.fieldName} · revizyon {s.pdf.revision}
                  {s.pdf.subFilter && <> · {s.pdf.subFilter}</>}
                </p>
              )}
              <p className="font-medium text-sm">{s.subjectName}</p>
              <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
                İhraç eden: {s.issuerName}
              </p>
              <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                <KV label="Seri No"       value={s.serialNumber} mono />
                <KV label="Parmak izi"    value={s.thumbprint} mono />
                <KV label="Digest"        value={s.digestAlgorithm} />
                <KV label="İmza zamanı"   value={s.signingTime ? new Date(s.signingTime).toLocaleString('tr-TR') : '—'} />
                <KV label="Geçerli"
                    value={`${new Date(s.notBefore).toLocaleDateString('tr-TR')} → ${new Date(s.notAfter).toLocaleDateString('tr-TR')}`} />
                <KV label="Sertifika ref" value={s.signingCertificateReferenceValid ? 'OK' : 'mismatch'}
                    badge={s.signingCertificateReferenceValid ? 'badge-ok' : 'badge-err'} />
                {s.pdf && (
                  <KV label="İmza kapsamı"
                      value={s.pdf.coversWholeDocument ? 'Belgenin tamamı' : 'Kendi revizyonu'} />
                )}
                {s.pdf && !s.pdf.byteRangeConsistent && (
                  <KV label="ByteRange" value="tutarsız" badge="badge-err" />
                )}
                {s.longTerm && (
                  <KV label="İptal durumu"
                      value={revocationLabel(s.longTerm.revocationStatus)}
                      badge={s.longTerm.revocationStatus === 'Good' ? 'badge-ok'
                           : s.longTerm.revocationStatus === 'Revoked' ? 'badge-err' : undefined} />
                )}
              </div>
              {s.timestamp && (
                <div className="mt-3 rounded bg-[var(--color-bg-soft)] p-3 text-xs">
                  <p className="font-medium">RFC 3161 Zaman Damgası</p>
                  <p className="mt-1 text-[var(--color-fg-muted)]">
                    {new Date(s.timestamp.time).toLocaleString('tr-TR')}
                    {s.timestamp.tsaSubject && <> · TSA: {s.timestamp.tsaSubject}</>}
                  </p>
                  <p className="mt-1 text-[var(--color-fg-muted)]">
                    Imprint: <span className={s.timestamp.messageImprintValid ? 'text-emerald-600' : 'text-red-600'}>
                      {s.timestamp.messageImprintValid ? 'OK' : 'FAIL'}
                    </span>
                    {' · '}
                    TSA İmzası: <span className={s.timestamp.tsaSignatureValid ? 'text-emerald-600' : 'text-red-600'}>
                      {s.timestamp.tsaSignatureValid ? 'OK' : 'FAIL'}
                    </span>
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 border-t border-[var(--color-border)] pt-4 text-xs text-[var(--color-fg-dim)]">
        Bu sayfada doğrulama, W.Sign’ın <code>PadesVerifier</code> / <code>CadesVerifier</code> /{' '}
        <code>XadesVerifier</code> bileşenleriyle yapılır. Dosya hiçbir yerde saklanmaz, sadece
        doğrulama amaçlı tek seferlik işlenir.
      </div>
    </div>
  )
}

/**
 * PAdES belge özeti. `lastRevisionCoveredBySignature === false` alarm DEĞİLDİR: W.Sign'ın kendi
 * uzun dönem çıktısı (DSS) imzadan sonra ayrı bir revizyon olarak eklenir, ikinci imza da öyle.
 * Bu yüzden ek revizyonun ne olduğu ayırt edilerek anlatılır; "belge değiştirilmiş" denmez —
 * imzadan sonraki revizyonun içeriğini ayrıştıran tam DocMDP analizi henüz yapılmıyor.
 */
function DocumentSummary({ doc }: { doc: VerifyDocumentInfo }) {
  const appended = !doc.lastRevisionCoveredBySignature
  return (
    <div className="mt-6 rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-4">
      <div className="grid gap-2 text-xs sm:grid-cols-4">
        <KV label="Seviye"   value={doc.level} />
        <KV label="İmza"     value={`${doc.signatureCount} adet`} />
        <KV label="Revizyon" value={`${doc.totalRevisions} adet`} />
        <KV label="Belge zaman damgası" value={`${doc.documentTimestampCount} adet`} />
      </div>

      {doc.dss && (
        <p className="mt-3 text-xs text-[var(--color-fg-muted)]">
          Uzun dönem doğrulama verisi (DSS) gömülü: {doc.dss.certificateCount} sertifika,{' '}
          {doc.dss.crlCount} CRL, {doc.dss.ocspResponseCount} OCSP yanıtı. Bu belge, imzalayan
          sertifika süresi dolduktan sonra da çevrimdışı doğrulanabilir.
        </p>
      )}

      {appended && (
        <p className="mt-2 text-xs text-[var(--color-fg-muted)]">
          {doc.dss
            ? 'İmzadan sonra uzun dönem doğrulama verisi eklenmiş — bu ek revizyon imzayı bozmaz.'
            : 'İmzadan sonra belgeye ek bir revizyon eklenmiş. İmza kendi revizyonunu kapsar; sonradan eklenen içerik imza kapsamı dışındadır.'}
        </p>
      )}
    </div>
  )
}

function revocationLabel(status: string): string {
  if (status === 'Good')    return 'İptal edilmemiş'
  if (status === 'Revoked') return 'İPTAL EDİLMİŞ'
  return 'Bilinmiyor'
}

function KV({ label, value, mono, badge }: { label: string; value: string; mono?: boolean; badge?: string }) {
  return (
    <div>
      <p className="text-[var(--color-fg-dim)]">{label}</p>
      {badge
        ? <span className={`badge ${badge}`}>{value}</span>
        : <p className={`mt-0.5 ${mono ? 'font-mono break-all' : ''}`}>{value}</p>}
    </div>
  )
}

function InfoCard({ title, desc, muted }: { title: string; desc: string; muted?: boolean }) {
  return (
    <div className={`card ${muted ? 'opacity-60' : ''}`}>
      <h3 className="font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{desc}</p>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}
